import _regeneratorRuntime from "@babel/runtime/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { ErrorMessage } from "@components/atoms";
export var adyenNotNegativeConfirmationStatusCodes = ["Authorised", "AuthenticationFinished", "AuthenticationNotRequired", "Pending", "Received", "PresentToShopper"];
var messageDescription = "Adyen payment gateway error";
export var adyenErrorMessages = defineMessages({
  unknownPayment: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenErrorMessages.unknownPayment",
    defaultMessage: "Unknown payment submission error occured.",
    description: messageDescription
  },
  invalidPaymentSubmission: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenErrorMessages.invalidPaymentSubmission",
    defaultMessage: "Invalid payment submission.",
    description: messageDescription
  },
  cannotHandlePaymentConfirmation: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenErrorMessages.cannotHandlePaymentConfirmation",
    defaultMessage: "Payment gateway did not provide payment confirmation handler.",
    description: messageDescription
  },
  paymentMalformedConfirmationData: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenErrorMessages.paymentMalformedConfirmationData",
    defaultMessage: "Payment needs confirmation but data required for confirmation received from the server is malformed.",
    description: messageDescription
  },
  paymentNoConfirmationData: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenErrorMessages.paymentNoConfirmationData",
    defaultMessage: "Payment needs confirmation but data required for confirmation not received from the server.",
    description: messageDescription
  }
});
export var adyenConfirmationErrorMessages = defineMessages({
  error: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenConfirmationErrorMessages.error",
    defaultMessage: "Error processing payment occured.",
    description: messageDescription
  },
  refused: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenConfirmationErrorMessages.refused",
    defaultMessage: "The payment was refused. Try the payment again using a different payment method or card.",
    description: messageDescription
  },
  cancelled: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenConfirmationErrorMessages.cancelled",
    defaultMessage: "Payment was cancelled.",
    description: messageDescription
  },
  general: {
    "id": "@next.components.organisms.AdyenPaymentGateway.AdyenPaymentGateway.adyenConfirmationErrorMessages.general",
    defaultMessage: "Payment confirmation went wrong.",
    description: messageDescription
  }
});
export function translateAdyenConfirmationError(status, intl) {
  switch (status) {
    case "Error":
      return intl.formatMessage(adyenConfirmationErrorMessages.error);

    case "Refused":
      return intl.formatMessage(adyenConfirmationErrorMessages.refused);

    case "Cancelled":
      return intl.formatMessage(adyenConfirmationErrorMessages.cancelled);

    default:
      return intl.formatMessage(adyenConfirmationErrorMessages.general);
  }
}

var AdyenPaymentGateway = function AdyenPaymentGateway(_ref) {
  var _config$find, _config$find2;

  var config = _ref.config,
      formRef = _ref.formRef,
      scriptConfig = _ref.scriptConfig,
      styleConfig = _ref.styleConfig,
      processPayment = _ref.processPayment,
      submitPayment = _ref.submitPayment,
      submitPaymentSuccess = _ref.submitPaymentSuccess,
      errors = _ref.errors,
      onError = _ref.onError;
  var intl = useIntl();
  var adyenClientKey = config === null || config === void 0 ? void 0 : (_config$find = config.find(function (_ref2) {
    var field = _ref2.field;
    return field === "client_key";
  })) === null || _config$find === void 0 ? void 0 : _config$find.value;
  var adyenConfig = config === null || config === void 0 ? void 0 : (_config$find2 = config.find(function (_ref3) {
    var field = _ref3.field;
    return field === "config";
  })) === null || _config$find2 === void 0 ? void 0 : _config$find2.value;
  var parsedAdyenConfig = adyenConfig && JSON.parse(adyenConfig);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      dropin = _useState2[0],
      setDropin = _useState2[1];

  var gatewayRef = useRef(null);
  useEffect(function () {
    if (adyenClientKey && parsedAdyenConfig && !dropin && gatewayRef.current) {
      var link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = styleConfig.src;
      link.integrity = styleConfig.integrity;
      link.crossOrigin = styleConfig.crossOrigin;
      document.body.appendChild(link);
      var script = document.createElement("script");
      script.src = scriptConfig.src;
      script.integrity = scriptConfig.integrity;
      script.crossOrigin = scriptConfig.crossOrigin;
      script.async = true;
      script.onload = initAdyenGatewayHandlers; // Wait until the script is loaded before initiating AdyenCheckout

      document.body.appendChild(script);
    }

    return function () {};
  }, [adyenClientKey, parsedAdyenConfig, gatewayRef.current]);

  var initAdyenGatewayHandlers = function initAdyenGatewayHandlers() {
    var configuration = adyenClientKey && adyenConfig && {
      locale: navigator.language,
      environment: "test",
      clientKey: adyenClientKey,
      paymentMethodsResponse: parsedAdyenConfig,
      showPayButton: false,
      onSubmit: onSubmitAdyenForm,
      onAdditionalDetails: onSubmitAdyenForm,
      onError: onAdyenError
    };
    var checkout = configuration && new window.AdyenCheckout(configuration);
    var dropinElement = checkout === null || checkout === void 0 ? void 0 : checkout.create("dropin");

    if (dropinElement && !dropin && gatewayRef.current) {
      dropinElement === null || dropinElement === void 0 ? void 0 : dropinElement.mount(gatewayRef.current);
      setDropin(dropinElement);
    }
  };

  var onSubmitAdyenForm = /*#__PURE__*/function () {
    var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(state, dropin) {
      var _payment$errors, payment, paymentAction;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (state === null || state === void 0 ? void 0 : state.isValid) {
                _context.next = 4;
                break;
              }

              onError([new Error(intl.formatMessage(adyenErrorMessages.invalidPaymentSubmission))]);
              _context.next = 8;
              break;

            case 4:
              _context.next = 6;
              return submitPayment(state === null || state === void 0 ? void 0 : state.data);

            case 6:
              payment = _context.sent;

              if ((_payment$errors = payment.errors) === null || _payment$errors === void 0 ? void 0 : _payment$errors.length) {
                onError(payment.errors);
              } else if (!(payment === null || payment === void 0 ? void 0 : payment.confirmationNeeded)) {
                submitPaymentSuccess(payment === null || payment === void 0 ? void 0 : payment.order);
              } else if (!(dropin === null || dropin === void 0 ? void 0 : dropin.handleAction)) {
                onError([new Error(intl.formatMessage(adyenErrorMessages.cannotHandlePaymentConfirmation))]);
              } else if (!(payment === null || payment === void 0 ? void 0 : payment.confirmationData)) {
                onError([new Error(intl.formatMessage(adyenErrorMessages.paymentNoConfirmationData))]);
              } else {
                try {
                  paymentAction = JSON.parse(payment.confirmationData);
                } catch (parseError) {
                  onError([new Error(intl.formatMessage(adyenErrorMessages.paymentMalformedConfirmationData))]);
                }

                try {
                  dropin.handleAction(paymentAction);
                } catch (error) {
                  onError([new Error(error)]);
                }
              }

            case 8:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function onSubmitAdyenForm(_x, _x2) {
      return _ref4.apply(this, arguments);
    };
  }();

  var onAdyenError = function onAdyenError(error) {
    if (error === null || error === void 0 ? void 0 : error.error) {
      onError([{
        message: error.error
      }]);
    } else {
      onError([new Error(intl.formatMessage(adyenErrorMessages.unknownPayment))]);
    }
  };

  useEffect(function () {
    if (dropin) {
      var _ref5;

      (_ref5 = formRef === null || formRef === void 0 ? void 0 : formRef.current) === null || _ref5 === void 0 ? void 0 : _ref5.addEventListener("submitComplete", function () {
        dropin.submit();
      });
    }

    return function () {};
  }, [formRef, dropin]);

  var handleSubmit = function handleSubmit(event) {
    event.preventDefault();
    processPayment();
  };

  return React.createElement("form", {
    ref: formRef,
    onSubmit: handleSubmit
  }, React.createElement("div", {
    ref: gatewayRef
  }), React.createElement(ErrorMessage, {
    errors: errors
  }));
};

export { AdyenPaymentGateway };